import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import TrackingVehicleDevice from 'api/tracking/models/TrackingVehicleDevice';
import Logger from 'common/services/Logger';
import Button from 'common/components/button/Button';
import styles from './VehiclesDeviceDetails.module.scss';
import InputError from 'common/components/inputError/InputError';
import Input from 'common/components/input/Input';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import FormController from 'common/components/formController/FormController';
import { useForm } from 'react-hook-form';
import { DEFAULT_INPUT_RULES_WITH_REQUIRED, LOGGER_LOG_TYPE, removeAccents } from 'Config';
import GreyArrow from 'assets/svg/Arrow-grey.svg';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import TrackingService from 'api/tracking/TrackingService';
import SelectController from 'common/components/select/SelectController';
import InputController from 'common/components/input/InputController';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';

type Props = {
};

const VehiclesDeviceDetails: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const history = useHistory();
    // eslint-disable-next-line
    let { type, itemId } = useParams<{ type: string, itemId: string }>();

    const [deviceList, setDeviceList] = useState<SelectValueLabel[]>([]);

    const [vehicleDevice, setVehicleDevice] = useState<TrackingVehicleDevice | null>(null);

    const form = useForm<TrackingVehicleDevice>({ shouldUnregister: false, defaultValues: { ...vehicleDevice } });

    const { register, handleSubmit, errors, reset } = form;

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVehicleDevicesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_GPS_WRITE']);

    const [showModal, setShowModal] = useState<boolean>(false);
    const [item, setItem] = useState<TrackingVehicleDevice | null>(null);

    const getData = async () => {
        Loading.show();
        try {
            if (itemId) {
                const model = await TrackingService.getVehicleDevice(itemId);             
                setVehicleDevice(model);
                reset(model);
            }
            const imeis = await TrackingService.getDeviceImeisList();
            const options: SelectValueLabel[] = imeis.map(x => ({ value: x.id, label: x.code }) as SelectValueLabel);
            setDeviceList(options);
            Loading.hide();

        } catch (error) {
            addToast(t('common.messages.error_load_info'), {
                appearance: 'error',
            });
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get vehicle model`, error);
            Loading.hide();
        }
    }

    const onPreSubmit = async (formData: TrackingVehicleDevice) => {
        if (formData.deviceId && formData.deviceId !== vehicleDevice?.deviceId) {
            setItem(formData);
            setShowModal(true);
        }
        else {
            await onSubmit(formData, true);
        }
    }

            
    const onSubmit = async (formData: TrackingVehicleDevice, keepHistory: boolean) => {
        try {
            Loading.show();
            if (vehicleDevice != null) {
                await TrackingService.changeVehicleDevice(vehicleDevice.id, formData.deviceId, keepHistory, formData.km);
            } 
            Loading.hide();

            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            history.goBack();
        } catch (error) {
            Loading.hide();
            
            if(error?.response?.status === 409){
                addToast(t('common.messages.name_already_exists'), { appearance: 'warning' });
                return;
            }

            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't create or update vehicle model`, error);
        }
    };
   
    useEffect(() => {
        getData();
    }, []);

    const onGoBack = () => {
        if(vehicleDevice){
            reset(vehicleDevice);
        }
        history.goBack();
    }

    
        const onNo = async () => {            
            if (item) {
                await onSubmit(item, true);
                setShowModal(false);         
            }
        };
    
        const onYes = async () => {                      
            if (item) {
                await onSubmit(item, false);
                setShowModal(false);         
            }
        };



    return (
        <div>
            <div className={styles.pageNameContainer}>
                <div className={styles.pageNameContent} onClick={() => history.push(`/settings/vehicleDevices/list`)}>
                    <img className={styles.pageNameContentImage} src={GreyArrow} />
                    <span className={styles.pageNameContentText}>
                        {t('settings.vehicle_devices')}
                    </span>
                </div>
            </div>

            <form className={styles.form} onSubmit={handleSubmit(onPreSubmit)}>
                <FormController>
                    <Row>
                        <Col xs={12} md={12} lg={12} className={styles.col}>
                            <FormItem>
                                <Label className={styles.label}>{t('vehicle_devices.registration_number')} {type !== 'details' ? '*' : ''}</Label>
                                <Input
                                    name='registrationNumber'
                                    placeholder={t('vehicle_devices.registration_number')}
                                    ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED })}
                                    disabled={true}
                                />
                                <InputError error={errors.registrationNumber} maxLength={250}/>
                            </FormItem>
                        </Col>
                        <Col xs={12} md={6} lg={6} className={styles.col}>
                            <FormItem>
                                <Label className={styles.label}>{t('vehicle_devices.device')}</Label>
                                <SelectController
                                    form={form}
                                    name='deviceId'
                                    menuPortalTarget={document.querySelector('body')}
                                    options={deviceList}
                                    placeholder={t('vehicle_devices.device')}
                                    isClearable={true}
                                    rules={{ required: false }}
                                    isDisabled={type === 'details'}
                                    filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                />
                                <InputError error={errors.deviceId} />
                            </FormItem>
                        </Col>
                        
                        { type === 'edit' && vehicleDevice && vehicleDevice.hasKmFeature &&<Col xs={3} className={styles.col}>

                            <FormItem>
                                <Label className={styles.label}>{t('vehicle_devices.km')}</Label>
                                <InputController
                                    name="km"
                                    type="number"
                                    form={form as any}
                                    disabled={false}
                                    placeholder={t('vehicle_devices.km')}                                    
                                />
                                <InputError error={errors.km} maxLength={250} />
                            </FormItem>
                        </Col>}

                    </Row>
                </FormController>

                <div className={styles.buttonContainer}>
                    <Button
                        preset={'secondary'}
                        type='button'
                        text={t('common.cancel')}
                        onClick={() => onGoBack()}
                    />                    
                    {type === 'details' && hasVehicleDevicesWritePolicy && <Button type='button' text={t('common.edit')} onClick={() => history.push(`/settings/vehicleDevices/edit/${itemId}`)} />}
                    {type !== 'details' && hasVehicleDevicesWritePolicy && <Button type='submit' text={t('common.save')} />}
                </div>

                <QuestionYesNo onNo={onNo} onYes={onYes} isVisible={showModal} message={t('common.messages.remove_gps_history')}/>
            </form>
        </div>
    );
};

export default VehiclesDeviceDetails;
